import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { errorText } from "./api";
import {
  countChanges,
  decidedByText,
  FEATURE_LABELS,
  fromTriState,
  isNum,
  parseSamplePct,
  toTriState,
  TRI_STATE_LABELS,
  type TriState,
} from "./format";
import type { EffectiveConfig, FeatureInfo, FeatureKey, FeatureSettings } from "./types";
import { FEATURE_KEYS } from "./types";

type Draft = Record<FeatureKey, { state: TriState; pct: string }>;

const toDraft = (settings: FeatureSettings): Draft =>
  Object.fromEntries(FEATURE_KEYS.map((key) => [key, {
    state: toTriState(settings[key]?.enabled),
    pct: isNum(settings[key]?.sample_pct) ? String(settings[key]!.sample_pct) : "",
  }])) as Draft;

function fromDraft(draft: Draft): { settings: FeatureSettings; invalid: FeatureKey[] } {
  const settings: FeatureSettings = {};
  const invalid: FeatureKey[] = [];
  FEATURE_KEYS.forEach((key) => {
    const pct = parseSamplePct(draft[key].pct);
    if (pct === "invalid") invalid.push(key);
    settings[key] = { enabled: fromTriState(draft[key].state), sample_pct: pct === "invalid" ? null : pct };
  });
  return { settings, invalid };
}

export function FeatureSettingsEditor({
  scope,
  features,
  original,
  platform,
  effective,
  readOnly,
  onSave,
}: {
  scope: "organization" | "agent";
  features: FeatureInfo[] | undefined;
  original: FeatureSettings;
  platform: FeatureSettings;
  effective: EffectiveConfig | undefined;
  readOnly: boolean;
  /** Receives every feature: the service replaces the scope's whole map on PUT. */
  onSave: (features: FeatureSettings) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(original));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => setDraft(toDraft(original)), [original]);

  const info = useMemo(() => new Map((features ?? []).map((f) => [f.key, f])), [features]);
  const { settings, invalid } = fromDraft(draft);
  const changeCount = countChanges(original, settings);
  const labels = TRI_STATE_LABELS[scope];

  const update = (key: FeatureKey, patch: Partial<Draft[FeatureKey]>) => {
    setSaved(false);
    setDraft((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(settings);
      setSaved(true);
    } catch (e) {
      setError(errorText(e, "The settings could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <ul className="divide-y divide-line rounded-xl border border-line">
        {FEATURE_KEYS.map((key) => {
          const label = info.get(key)?.label || FEATURE_LABELS[key];
          const description = info.get(key)?.description;
          const unavailable = platform[key]?.enabled === false;
          const cap = platform[key]?.sample_pct;
          const eff = effective?.[key];
          const selectId = `ca-${scope}-${key}-state`;
          const pctId = `ca-${scope}-${key}-pct`;
          return (
            <li key={key} className="grid gap-3 px-3 py-3 md:grid-cols-[1fr_auto_auto] md:items-start">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-ink">{label}</span>
                  {unavailable ? <Badge tone="neutral">Not available on your plan</Badge> : null}
                  {isNum(cap) ? <span className="text-xs text-ink-3">TechLadder limit: {cap}% of calls</span> : null}
                </div>
                {description ? <p className="text-xs text-ink-3">{description}</p> : null}
                {eff ? (
                  <p className="mt-1 text-xs text-ink-2">
                    <Badge tone={eff.enabled ? "success" : "neutral"}>{eff.enabled ? `On · ${eff.sample_pct}% of calls` : "Off"}</Badge>{" "}
                    {decidedByText(eff.decided_by, eff.enabled)}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor={selectId} className="text-[11px] font-medium text-ink-3">{label} setting</label>
                <select
                  id={selectId}
                  value={draft[key].state}
                  disabled={readOnly || unavailable}
                  onChange={(e) => update(key, { state: e.target.value as TriState })}
                  className="h-9 rounded-lg border border-line bg-elevated px-2 text-sm text-ink disabled:opacity-60"
                >
                  {(["unset", "on", "off"] as TriState[]).map((state) => <option key={state} value={state}>{labels[state]}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor={pctId} className="text-[11px] font-medium text-ink-3">{label} sample %</label>
                <input
                  id={pctId}
                  type="number"
                  min={0}
                  max={100}
                  inputMode="decimal"
                  placeholder="Inherit"
                  value={draft[key].pct}
                  disabled={readOnly || unavailable}
                  aria-invalid={invalid.includes(key) || undefined}
                  onChange={(e) => update(key, { pct: e.target.value })}
                  className="h-9 w-28 rounded-lg border border-line bg-elevated px-2 text-sm text-ink disabled:opacity-60 aria-[invalid=true]:border-danger"
                />
              </div>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-ink-3">
        Sampling takes the lowest percentage set at any level. A blank sample % inherits from the level above (100% when nothing is set).
      </p>
      {!readOnly ? (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving || changeCount === 0 || invalid.length > 0}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            {changeCount ? `Save ${changeCount} change${changeCount === 1 ? "" : "s"}` : "No changes"}
          </button>
          {invalid.length ? <p role="alert" className="text-sm text-danger">Sample % must be between 0 and 100.</p> : null}
          {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
          {saved && !changeCount ? <p role="status" className="text-sm text-success">Saved.</p> : null}
        </div>
      ) : null}
    </div>
  );
}
